import { createContext, useContext, ReactNode, useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { HubConnection, HubConnectionBuilder, HubConnectionState } from '@microsoft/signalr';
import { useAuth } from './AuthContext';
import { useAppData, UserRole } from './AppDataContext';
import { useNavigate } from 'react-router-dom';

const AUTHENTICATED_USER_HUB_URL = `${import.meta.env.VITE_PORTAL_API_URL}/authenticated-user-hub`;
const NOTIFICATION_HUB_URL = `${import.meta.env.VITE_NOTIFICATION_HUB_URL}/notificationHub`;

interface SignalRContextType {
  isConnected: boolean;
  isNotificationConnected: boolean;
  hubConnection: HubConnection | null;
  notificationHubConnection: HubConnection | null;
  connect: (accessToken: string) => Promise<void>;
  disconnect: () => Promise<void>;
}

const SignalRContext = createContext<SignalRContextType | undefined>(undefined);
const clientProductId = import.meta.env.VITE_PRODUCT_ID;
export function SignalRProvider({ children }: { children: ReactNode }) {
  const { microsoftToken, getGromToken } = useAuth();
  const { fetchQueuesWithRole, removeAgentByMicrosoftId, queues } = useAppData();
  const [hubConnection, setHubConnection] = useState<HubConnection | null>(null);
  const [notificationHubConnection, setNotificationHubConnection] = useState<HubConnection | null>(null);
  const [isConnected, setIsConnected] = useState<boolean>(false);
  const [isNotificationConnected, setIsNotificationConnected] = useState<boolean>(false);
  const [isSubscribed, setIsSubscribed] = useState<boolean>(false);
  const navigate = useNavigate();
  const { userRole } = useAppData();

  // Refs for auto-reconnect logic
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 5;
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isReconnecting = useRef(false);

  const invokeAuthenticatedAndSubscribe = useCallback(async (hubConnection: HubConnection, accessToken: string) => {
    if (!hubConnection || !userRole || !isConnected || isSubscribed) {
      return;
    }

    const handleEventReceived = (eventType: string = '') => {
      if (['DirectAgentGroupChanged', 'UserRoleChanged'].includes(eventType)) {
        console.log(`SignalR: ${eventType} event received, refreshing queues...`);
        fetchQueuesWithRole();
      }

      if (eventType === 'UserChanged') {
        getGromToken(accessToken);
        // Check current path is auth-error. Will redirect to home page
        if (window.location.pathname === '/auth-error') {
          navigate('/');
        }
      }
    }

    try {
      await hubConnection.invoke('SubcribeAuthenticatedUser', accessToken);
      await hubConnection.invoke('SubscribeToClientProductAsync', Number(clientProductId));

      // Subscribe to user changed
      hubConnection.on('UserChanged', (eventType: string) => {
        handleEventReceived(eventType);
      });

      hubConnection.on('AdminNotification', (eventType: string) => {
        if (userRole !== UserRole.ADMIN) return;
        handleEventReceived(eventType);
      });

      setIsSubscribed(true);
      console.log('SignalR: Successfully invoked authenticated and subscribed events');
    } catch (error) {
      console.error('SignalR: Failed to invoke authenticated and subscribe:', error);
      throw error;
    }
  }, [isConnected, getGromToken, isSubscribed, userRole, fetchQueuesWithRole, removeAgentByMicrosoftId, navigate]);

  const connectRef = useRef<() => Promise<void>>();
  const scheduleReconnectRef = useRef<() => void>();
  const lastHiddenAtRef = useRef<number | null>(null);
  const SUSPEND_FORCE_RESTART_MS = 60000; // 60s threshold to treat as suspended
  const resumeRecoveryInProgressRef = useRef<boolean>(false);

  const connect = useCallback(async () => {
    if (isConnected || isReconnecting.current) {
      return;
    }

    isReconnecting.current = true;
    console.log('SignalR: Connecting to', AUTHENTICATED_USER_HUB_URL);

    const hubConnection = new HubConnectionBuilder()
      .withUrl(AUTHENTICATED_USER_HUB_URL)
      .withAutomaticReconnect([0, 2000, 10000, 30000, 60000]) // Custom retry intervals
      .build();

    // Heartbeat/timeout tuning to detect half-open sockets after resume
    // Keep defaults if already set by library, but explicitly set for clarity
    hubConnection.keepAliveIntervalInMilliseconds = 15000; // send/expect activity at least every 15s
    hubConnection.serverTimeoutInMilliseconds = 45000; // consider dead if no activity for 45s

    // Add connection event handlers
    hubConnection.onclose((error) => {
      console.log('SignalR: Connection closed', error);
      setIsConnected(false);
      setIsSubscribed(false);

      // Auto-reconnect if not manually disconnected
      if (microsoftToken && !isReconnecting.current) {
        setTimeout(() => scheduleReconnectRef.current?.(), 1000); // Delay to avoid immediate reconnection
      }
    });

    hubConnection.onreconnecting((error) => {
      console.log('SignalR: Reconnecting...', error);
      setIsConnected(false);
    });

    hubConnection.onreconnected((connectionId) => {
      console.log('SignalR: Reconnected with connection ID:', connectionId);
      setIsConnected(true);
      isReconnecting.current = false;
      reconnectAttempts.current = 0;

      // Re-subscribe when reconnected
      if (microsoftToken) {
        invokeAuthenticatedAndSubscribe(hubConnection, microsoftToken);
      }
    });

    try {
      await hubConnection.start();
      setIsConnected(true);
      setHubConnection(hubConnection);
      isReconnecting.current = false;
      reconnectAttempts.current = 0;
      console.log('SignalR: Connected successfully');
    } catch (error) {
      console.error('SignalR: Connection failed', error);
      setIsConnected(false);
      setHubConnection(null);
      isReconnecting.current = false;
      throw error;
    }
  }, [isConnected, microsoftToken, invokeAuthenticatedAndSubscribe]);

  const scheduleReconnect = useCallback(() => {
    if (reconnectAttempts.current >= maxReconnectAttempts) {
      console.log('SignalR: Max reconnect attempts reached, giving up');
      return;
    }

    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }

    const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 30000); // Exponential backoff, max 30s
    reconnectAttempts.current++;

    console.log(`SignalR: Scheduling reconnect attempt ${reconnectAttempts.current} in ${delay}ms`);

    reconnectTimeoutRef.current = setTimeout(async () => {
      if (microsoftToken && !isConnected && connectRef.current) {
        try {
          await connectRef.current();
        } catch (error) {
          console.error('SignalR: Reconnect attempt failed:', error);
          scheduleReconnect(); // Try again
        }
      }
    }, delay);
  }, [microsoftToken, isConnected]);

  // Connect to notification hub for queue changes
  const connectNotificationHub = useCallback(async () => {
    if (isNotificationConnected || notificationHubConnection) {
      return;
    }

    console.log('SignalR: Connecting to notification hub', NOTIFICATION_HUB_URL);

    const connection = new HubConnectionBuilder()
      .withUrl(NOTIFICATION_HUB_URL)
      .withAutomaticReconnect([0, 2000, 10000, 30000, 60000])
      .build();

    // Heartbeat/timeout tuning for notification hub as well
    connection.keepAliveIntervalInMilliseconds = 15000;
    connection.serverTimeoutInMilliseconds = 45000;

    // Handle QueueChanged events
    connection.on('QueueChanged', (queueId: string) => {
      console.log(`SignalR: QueueChanged event received for queueId: ${queueId}`);

      // Check if the queueId exists in current queues
      const queueExists = queues.some(queue =>
        queue.microsoftId === queueId ||
        queue.microsoftId === queueId.toString()
      );

      if (queueExists) {
        console.log(`SignalR: Queue ${queueId} found in current queues, refreshing...`);
        fetchQueuesWithRole();
      } else {
        console.log(`SignalR: Queue ${queueId} not found in current queues, skipping refresh`);
      }
    });

    // Handle agentDeleted events (sent from notification hub)
    connection.on('agentDeleted', (data: { userId: string }) => {
      console.log('SignalR Notification Hub: agentDeleted event received:', data);
      if (data?.userId) {
        removeAgentByMicrosoftId(data.userId);
      }
    });

    // Connection event handlers
    connection.onclose((error) => {
      console.log('SignalR Notification Hub: Connection closed', error);
      setIsNotificationConnected(false);
    });

    connection.onreconnecting((error) => {
      console.log('SignalR Notification Hub: Reconnecting...', error);
      setIsNotificationConnected(false);
    });

    connection.onreconnected((connectionId) => {
      console.log('SignalR Notification Hub: Reconnected with connection ID:', connectionId);
      setIsNotificationConnected(true);
    });

    try {
      await connection.start();
      setIsNotificationConnected(true);
      setNotificationHubConnection(connection);
      console.log('SignalR Notification Hub: Connected successfully');
    } catch (error) {
      console.error('SignalR Notification Hub: Connection failed', error);
      setIsNotificationConnected(false);
      setNotificationHubConnection(null);
      throw error;
    }
  }, [isNotificationConnected, notificationHubConnection, queues, fetchQueuesWithRole, removeAgentByMicrosoftId]);

  const disconnect = useCallback(async () => {
    const promises = [];

    // Disconnect main hub
    if (hubConnection && isConnected) {
      promises.push(
        hubConnection.stop().then(() => {
          setIsConnected(false);
          setHubConnection(null);
          setIsSubscribed(false);
          console.log('SignalR: Main hub disconnected successfully');
        }).catch(error => {
          console.error('SignalR: Main hub disconnect failed', error);
        })
      );
    }

    // Disconnect notification hub
    if (notificationHubConnection && isNotificationConnected) {
      promises.push(
        notificationHubConnection.stop().then(() => {
          setIsNotificationConnected(false);
          setNotificationHubConnection(null);
          console.log('SignalR: Notification hub disconnected successfully');
        }).catch(error => {
          console.error('SignalR: Notification hub disconnect failed', error);
        })
      );
    }

    // Clear any pending reconnect attempts
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    // Reset reconnecting state
    isReconnecting.current = false;
    reconnectAttempts.current = 0;

    try {
      await Promise.all(promises);
      console.log('SignalR: All hubs disconnected successfully');
    } catch (error) {
      console.error('SignalR: Disconnect failed', error);
      throw error;
    }
  }, [hubConnection, isConnected, notificationHubConnection, isNotificationConnected]);

  // Invoke authenticated and subscribe events when connection is established and microsoft token is available
  useEffect(() => {
    if (isConnected && hubConnection && microsoftToken) {
      invokeAuthenticatedAndSubscribe(hubConnection, microsoftToken);
    }
  }, [isConnected, hubConnection, microsoftToken, invokeAuthenticatedAndSubscribe]);

  // Auto connect when microsoft token is available
  useEffect(() => {
    if (microsoftToken && !isConnected) {
      // Reset retry attempts when auto-connecting
      reconnectAttempts.current = 0;
      connect().catch((error) => {
        console.error('Failed to auto-connect SignalR:', error);
      });
    }
  }, [microsoftToken, isConnected, connect]);

  // Auto connect to notification hub when queues are available
  useEffect(() => {
    if (queues.length > 0 && !isNotificationConnected) {
      connectNotificationHub().catch((error) => {
        console.error('Failed to auto-connect to notification hub:', error);
      });
    }
  }, [queues, isNotificationConnected, connectNotificationHub]);

  // Update refs when functions change
  useEffect(() => {
    connectRef.current = connect;
    scheduleReconnectRef.current = scheduleReconnect;
  }, [connect, scheduleReconnect]);

  // Handle visibility and network changes to recover from tab suspend/OS sleep
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        lastHiddenAtRef.current = Date.now();
        return;
      }

      const hiddenForMs = lastHiddenAtRef.current ? Date.now() - lastHiddenAtRef.current : 0;
      lastHiddenAtRef.current = null;

      // If tab was suspended for long enough, force a clean restart of connections
      if (hiddenForMs > SUSPEND_FORCE_RESTART_MS) {
        console.log(`SignalR: Tab resumed after ${hiddenForMs}ms hidden. Forcing hub restart to avoid half-open.`);
        // Guard against concurrent resume recovery
        if (resumeRecoveryInProgressRef.current) return;
        resumeRecoveryInProgressRef.current = true;
        // Force stop both hubs, then reconnect if token is present
        (async () => {
          try {
            await disconnect();
          } catch (err) {
            console.warn('SignalR: Disconnect during resume failed', err);
          } finally {
            if (microsoftToken) {
              reconnectAttempts.current = 0;
              await connectRef.current?.();
            }
            resumeRecoveryInProgressRef.current = false;
          }
        })();
        return;
      }

      // If just briefly hidden, ensure healthy connection if it silently died
      if (microsoftToken) {
        const state = hubConnection?.state;
        if (!isConnected || state !== HubConnectionState.Connected) {
          console.log('SignalR: Page visible, ensuring connection...');
          reconnectAttempts.current = 0;
          connectRef.current?.();
        }
      }
    };

    const handleOnline = () => {
      if (microsoftToken) {
        const state = hubConnection?.state;
        if (!isConnected || state !== HubConnectionState.Connected) {
          console.log('SignalR: Browser online, reconnecting hubs...');
          reconnectAttempts.current = 0;
          connectRef.current?.();
          // Notification hub reconnect handled by effect when isNotificationConnected is false
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('online', handleOnline);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('online', handleOnline);
    };
  }, [microsoftToken, isConnected, hubConnection, disconnect]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (hubConnection && isConnected && notificationHubConnection && isNotificationConnected) {
        disconnect();
      }
    };
  }, [hubConnection, isConnected, notificationHubConnection, isNotificationConnected, disconnect]);

  const value = useMemo(() => ({
    isConnected,
    isNotificationConnected,
    hubConnection,
    notificationHubConnection,
    connect,
    disconnect
  }), [
    isConnected,
    isNotificationConnected,
    hubConnection,
    notificationHubConnection,
    connect,
    disconnect
  ]);

  return <SignalRContext.Provider value={value}>{children}</SignalRContext.Provider>;
}

export function useSignalR() {
  const context = useContext(SignalRContext);
  if (context === undefined) {
    throw new Error('useSignalR must be used within a SignalRProvider');
  }
  return context;
}
